import { useState, useEffect, useMemo } from 'react';
import { LoadingTimeout } from '../components/ui/LoadingTimeout';
import { HeroSection } from '../components/Home/HeroSection';
import { NoResidenceJoin } from '../components/Home/NoResidenceJoin';
import { NewsSection } from '../components/Home/NewsSection';
import { EventsSection } from '../components/Home/EventsSection';
import { DocumentsSection } from '../components/Home/DocumentsSection';
import { BoardMessage } from '../components/Home/BoardMessage';
import { NewsFeed } from '../components/Home/NewsFeed';
import { supabase } from '../lib/supabase';
import { NewsItem } from '../types/news';
import { PageWrapper } from '../components/ui/PageWrapper';
import { useUser } from '../contexts/UserContext';
import { getCachedPinnedMessage, setCachedResidenceData } from '../utils/residenceCache';
import { useQuery, useQueryClient } from '@tanstack/react-query';


// Helper function to format date
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('no', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
  }).replace(/\./g, '.');
};

export function Home() {
  const [pinnedMessage, setPinnedMessage] = useState<NewsItem | null>(null);
  const { currentResidence, membershipLoading } = useUser();
  const hasResidence = Boolean(currentResidence?.residence_id);
  const residenceId = currentResidence?.residence_id;
  const queryClient = useQueryClient();

  // stripped render logging

  // Prefetch pinned + news + documents + events når residenceId blir tilgjengelig
  useEffect(() => {
    if (!residenceId) return;

    void queryClient.prefetchQuery({
      queryKey: ['residence', residenceId, 'pinned'],
      queryFn: async (): Promise<NewsItem | null> => {
        const cachedPinned = getCachedPinnedMessage(residenceId);
        if (cachedPinned) return cachedPinned as NewsItem;
        const { data, error } = await supabase
          .from('residence_news')
          .select('*')
          .eq('residence_id', residenceId)
          .eq('is_pinned', true)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (error || !data) return null;
        const formatted: NewsItem = { ...data, date: formatDate(data.created_at || '') } as NewsItem;
        setCachedResidenceData(residenceId, { pinned_message: formatted });
        return formatted;
      }
    });

    void queryClient.prefetchQuery({
      queryKey: ['residence', residenceId, 'news'],
      queryFn: async (): Promise<NewsItem[]> => {
        const { data, error } = await supabase
          .from('residence_news')
          .select('*')
          .eq('residence_id', residenceId)
          .order('created_at', { ascending: false });
        if (error) return [];
        const formatted: NewsItem[] = (data || []).map((item: any) => ({
          ...item,
          date: formatDate(item.created_at || ''),
        }));
        setCachedResidenceData(residenceId, { news: formatted });
        return formatted;
      }
    });

    void queryClient.prefetchQuery({
      queryKey: ['residence', residenceId, 'documents'],
      queryFn: async (): Promise<any[]> => {
        const { data, error } = await supabase
          .from('all_files')
          .select('id, name, file_url, category_id, is_important, visibility, created_at')
          .eq('residence_id', residenceId)
          .eq('visibility', 'community')
          .eq('is_important', true)
          .order('created_at', { ascending: false });
        if (error) return [];
        setCachedResidenceData(residenceId, { documents: data || [] });
        return (data || []);
      }
    });

    void queryClient.prefetchQuery({
      queryKey: ['residence', residenceId, 'events'],
      queryFn: async (): Promise<any[]> => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return [];

        // 1) Hent brukerens lagrede upcoming events
        // 2) Hent styreopprettede events for valgt bolig, framtidige
        // 3) Hent events hvor brukeren har meldt seg på (attending)
        const [upcomingResult, boardResult, attendingResult] = await Promise.all([
          supabase
            .from('upcoming_events')
            .select(`
              event_id,
              base_post!inner (
                id,
                title,
                content,
                board_initiated,
                created_at,
                forum_event!inner (
                  event_start,
                  event_end
                )
              )
            `)
            .eq('user_id', user.id)
            .order('added_at', { ascending: false }),

          supabase
            .from('base_post')
            .select(`
              id,
              title,
              content,
              board_initiated,
              created_at,
              residence_id,
              forum_type_id,
              forum_event!inner (
                event_start,
                event_end
              )
            `)
            .eq('board_initiated', true)
            .eq('forum_type_id', 2)
            .eq('residence_id', residenceId)
            .gte('forum_event.event_start', new Date().toISOString()),

          supabase
            .from('event_attendance')
            .select(`
              post_id,
              base_post!inner (
                id,
                title,
                content,
                board_initiated,
                created_at,
                residence_id,
                forum_type_id,
                forum_event!inner (
                  event_start,
                  event_end
                )
              )
            `)
            .eq('user_id', user.id)
            .eq('state', 'attending')
        ]);

        const upcomingEvents = upcomingResult.data || [];
        const boardEvents = boardResult.data || [];
        const allAttendingEvents = attendingResult.data || [];

        // Filtrer attending events til kun fremtidige
        const now = new Date().toISOString();
        const attendingEvents = allAttendingEvents
          .map((ae: any) => ae.base_post)
          .filter((e: any) => e != null && e.forum_event?.event_start && new Date(e.forum_event.event_start) >= new Date(now));

        // Samle alle events og dedupliser
        const allEvents = ([
          ...upcomingEvents.map((ue: any) => ue.base_post),
          ...boardEvents,
          ...attendingEvents
        ] as any[]);
        const uniqueEvents = (allEvents as any[]).filter((event: any, index: number, self: any[]) => 
          index === self.findIndex((e: any) => e?.id === event?.id)
        );

        return uniqueEvents;
      }
    });

    // Prefetch hero image
    void queryClient.prefetchQuery({
      queryKey: ['residence', residenceId, 'hero-image'],
      queryFn: async (): Promise<string | null> => {
        if (!residenceId) return null;
        const { data, error } = await supabase
          .from('user_data_view')
          .select('cover_image')
          .eq('residence_id', residenceId)
          .limit(1);
        if (error || !data?.[0]?.cover_image) return null;
        const imageUrl = data[0].cover_image.startsWith('http')
          ? data[0].cover_image
          : supabase.storage
              .from('residence_images')
              .getPublicUrl(data[0].cover_image)
              .data.publicUrl;
        return imageUrl;
      }
    });
  }, [residenceId, queryClient]);

  // React Query: pinned message per residence
  const { data: pinnedQueryData } = useQuery({
    queryKey: ['residence', residenceId, 'pinned'],
    enabled: Boolean(residenceId),
    queryFn: async (): Promise<NewsItem | null> => {
      if (!residenceId) return null;

      // Sjekk eventuell eksisterende cache først (valgfritt, beholdt for nå)
      const cachedPinned = getCachedPinnedMessage(residenceId);
      if (cachedPinned) {
        return cachedPinned as NewsItem;
      }

      const { data, error } = await supabase
        .from('residence_news')
        .select('*')
        .eq('residence_id', residenceId)
        .eq('is_pinned', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        // Ignorer 406-feil (ingen rader funnet) - det er ok å ikke ha pinned message
        if (error.code !== 'PGRST116') {
          console.error('Error fetching pinned message:', error);
        }
        return null;
      }

      if (!data) return null;

      const formattedMessage: NewsItem = {
        ...data,
        date: formatDate(data.created_at || '')
      } as NewsItem;

      // Oppdater lokal cache for andre seksjoner
      setCachedResidenceData(residenceId, { pinned_message: formattedMessage });
      return formattedMessage;
    }
  });

  useEffect(() => {
    setPinnedMessage(pinnedQueryData ?? null);
  }, [pinnedQueryData]);

  const handlePinChange = (updatedMessage: NewsItem | null) => {
    console.log('Pin changed to:', updatedMessage);
    if (updatedMessage) {
      setPinnedMessage({
        ...updatedMessage,
        date: formatDate(updatedMessage.created_at || '')
      });
    } else {
      setPinnedMessage(null);
    }
  };

  const mainContent = useMemo(() => (
    <div className="max-w-6xl mx-auto space-y-3 sm:space-y-4 p-4 sm:p-8">
      {hasResidence && <BoardMessage pinnedMessage={pinnedMessage} />}
      {hasResidence && <EventsSection />}
      {hasResidence && <NewsSection onPinChange={handlePinChange} />}
      {hasResidence && <DocumentsSection />}
      <NewsFeed />
    </div>
  ), [pinnedMessage, hasResidence]);

  // Vis loading kun mens membership-data lastes
  // AuthContext håndterer optimistisk auth, så vi trenger ikke vente på authLoading
  if (membershipLoading) {
    return (
      <PageWrapper variant="transparent">
        <div className="bg-condo-dark text-condo-light min-h-[calc(100vh-0px)] -mt-8 lg:-mt-20 -mx-4 flex items-center pb-0">
          <section className="w-full">
            <div className="max-w-3xl mx-auto p-4 sm:p-8">
              <LoadingTimeout 
                isLoading={membershipLoading} 
                variant="dark" 
                loadingText="Laster ditt dashboard..." 
              />
            </div>
          </section>
        </div>
      </PageWrapper>
    );
  }

  // Vis NoResidenceJoin kun når vi er sikre på at brukeren ikke har tilhørighet
  if (!hasResidence) {
    return (
      <PageWrapper variant="transparent">
        <div className="bg-condo-dark text-condo-light min-h-screen -mt-8 lg:-mt-20 -mx-4 flex items-center pb-0 relative">
          <section className="w-full">
            <div className="max-w-3xl mx-auto p-4 sm:p-8">
              <NoResidenceJoin />
            </div>
          </section>
          {/* Fade gradient fra condo-dark til condo-light nederst */}
          <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-condo-light/20 to-transparent pointer-events-none"></div>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper variant="transparent">
      <div className="-mt-16 lg:-mt-20 -mx-4">
        <HeroSection />
        {mainContent}
      </div>
    </PageWrapper>
  );
}
